import { isDefined, isEmpty } from '@sgme/fp';
import type { LanguageDownload } from 'components/shared/Document/MultiLanguageDocument';
import type { TenorFieldData } from 'components/shared/TenorField/TenorField';
import { DocumentFormat, FormDocument } from 'epic/common-form/Documentation';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Underlying } from 'models/underlying';
import { getDocumentDownloadError, isDownloading } from 'state/document/selector';
import type { DynamicFormInfos } from 'state/dynamic/infos/infos.model';
import { getFormEnrichedData, getFormInfos } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { sanitizeFilename } from 'utils/documents/filenames';

interface Language extends LanguageDownload {
  fileName?: string;
}

export interface BuildLanguagesDeps {
  state: AppState;
  tabId: string;
  documentType: string;
  documentation: FormDocument | undefined;
}

export function buildLanguages({ state, tabId, documentType, documentation }: BuildLanguagesDeps): Language[] {
  const docFormatVariations = documentation?.items;

  return (
    docFormatVariations?.map(({ language, format }) => {
      return {
        language: language ?? '',
        documentFormat: format,
        isDownloading: isDownloading(state, tabId, documentType, language, format),
        fileName: getDocumentNameByFormat(state, tabId, documentType, language, format),
        error: getDocumentDownloadError(state, tabId, documentType, language, format),
      };
    }) ?? []
  );
}

export function isTermsheet(documentType: string): boolean {
  return documentType.toLowerCase() === 'termsheet';
}

export function getDocumentNameByFormat(
  state: AppState,
  tabId: string,
  documentType: string,
  language: string,
  docFormat: DocumentFormat,
) {
  const fileExtension = docFormat === 'Word' ? 'doc' : 'pdf';
  const termSheetFileName = isTermsheet(documentType) ? getTermsheetFileName(state, tabId) : undefined;
  if (termSheetFileName !== undefined) {
    return `${termSheetFileName}.${fileExtension}`;
  }

  const documentTypeLiteral = extractDocumentType(documentType);
  if (documentTypeLiteral === undefined) {
    return undefined;
  }

  const formEnrichedData = getFormEnrichedData(state, tabId);
  const { TradeDate, Tenor, Currency, ProductSubtype, Underlyings, SelectedOnBehalfClient } = formEnrichedData;

  const tradeDate = TradeDate ? (TradeDate as string).substring(0, 10).replace(/-/g, '') : '';
  const tenor = Tenor ? extractTenor(Tenor as TenorFieldData) : '';
  const currency = Currency ?? '';
  const formInfo = getFormInfos(state, 'dynamic', tabId) as DynamicFormInfos;
  const instrumentContractName = formInfo?.instrumentContractName;
  const payoffType = ProductSubtype ?? instrumentContractName ?? '';
  const underlyings = Underlyings ? extractUnderlyings(Underlyings as Underlying[]) : undefined;
  const formattedUnderlyings = isDefined(underlyings) ? `[${underlyings}]` : '';
  const client = SelectedOnBehalfClient ? (SelectedOnBehalfClient as ISalesCounterpart).label : '';
  const duplicatedCharacterRegex = new RegExp(/\s{2,}/g);

  const filename =
    `${tradeDate} ${documentTypeLiteral} ${tenor} ${currency} ${payoffType} ${formattedUnderlyings} ${client} ${language}`
      .replace(duplicatedCharacterRegex, ' ')
      .trim() + `.${fileExtension}`;
  return sanitizeFilename(filename);
}

export function getTermsheetFileName(state: AppState, tabId: string) {
  const formData = getFormEnrichedData(state, tabId);
  const documentationTitle = formData.DocumentationTitle as [];
  const documentationTitleUnderlyings = formData.DocumentationTitleUnderlyings as [];

  if (isEmpty(documentationTitle) || isEmpty(documentationTitleUnderlyings)) {
    return undefined;
  }
  return `${documentationTitle}-${documentationTitleUnderlyings}`;
}

export function extractTenor(tenor: TenorFieldData): string {
  if (tenor.isCustomized) {
    return '';
  }
  if (tenor.currentValue === undefined) {
    return '';
  }
  const { unit, value } = tenor.currentValue;
  return `${value}${unit.charAt(0)}`;
}

export function extractUnderlyings(underlyings: Underlying[]) {
  return underlyings.length > 0 ? underlyings.map(underlying => underlying.id).join(', ') : undefined;
}

export function extractDocumentType(documentType: string) {
  if (isTermsheet(documentType)) {
    return 'TS';
  }

  switch (documentType) {
    case 'MarketingMaterial':
      return 'MARKETING';
    case 'Kid':
      return 'KID';
    case 'FinalTerms':
      return 'FT';
    case 'InformationFactSheet':
      return 'IFS';
    case 'SupplementDistributionAgreement':
      return 'eSDA';
    default:
      return documentType;
  }
}
